import { Application, FeathersService, Paginated, Service } from '@feathersjs/feathers/lib';
import { makeObservable, observable } from 'mobx';
import { rootStore } from 'src/app';
import client from 'src/feathers';
import { DataType, ITag, ITagSpace } from '../types';
import { Data } from './';


export class Tag extends Data<ITag> implements ITag {
  protected readonly space_link_service: FeathersService<Application<ITagSpace>, Service<ITagSpace>> = client.service("tag-space");

  title:        string   = null;
  color?:       string   = null;
  spaces:       number[] = [];
  
  constructor(data: ITag) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Tag, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually 
    makeObservable(this, {
      title: observable,
      color: observable
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here. 
    this.fromData(data);
  }

  public static create(data?: ITag): Tag {
    return new Tag(data);
  }

  protected fromData(data: ITag): void {
    super.fromData(data);
    this.title        = data.title;
    this.color        = data.color;
    this.spaces = data.spaces && Array.isArray(data.spaces) && data.spaces.map(w => Number(w)).filter(w => Boolean(w));
    this.spaces = this.spaces || [];
  }

  public toData(): any {
    return {
        ...super.toData(),
        title:        this.title,
        color:        this.color,
        created_by:   this.created_by || rootStore.authStore.getCurUserID,
        updated_by:   rootStore.authStore.getCurUserID,
    };
  }

  protected toPatchData(patch: Partial<ITag>): any {
    return {
        ...super.toPatchData(patch),
        title: "title" in patch ? patch.title : undefined,
        color: "color" in patch ? patch.color : undefined,
    };
  }

  public async linkSpace(space_id: number): Promise<void> {
    try {
      const tag_space: ITagSpace = {
        tag_id:       this.id,
        space_id:     space_id,
        workspace_id: this.workspace_id,
      }
      await this.space_link_service.create(tag_space);
    } catch (err) {
      console.error("Failed to create new tag", err);
      return;
    }
  }

  public async unlinkSpace(space_id: number): Promise<void> {
    try {
      const tag_space: Paginated<ITagSpace> = await this.space_link_service.find({
        query: {
          space_id: space_id,
          tag_id:   this.id
        }
      }) as unknown as Paginated<ITagSpace>;
      for(const ts of tag_space.data) { // In case accidentally more than one link was created
        await this.space_link_service.remove(ts.id);
      }
    } catch (err) {
      console.error("Failed to unlink tag from space", err);
      return;
    }
  }
}

